import { api } from "./api";
import { Post, PostSortField, sortPosts, showAvgRating, showPartialStars, renderRatingDistribution, renderWouldTakeAgainBadge } from "./posts";
import { getCompareIds, removeFromCompare, clearCompare } from "./compare";
import { getElement, showStars, EXAM_RATING_LABELS, renderSortMenu, SortMenuOption, renderEaseOfPassingText } from "./generics";
import { Comment, sortComments, whenAdded, CommentSortField, isFandomImportedComment, renderFandomSourceBadge, renderPsychSourceBadge, renderWpiaWikiSourceBadge, renderFilozofiaWikiSourceBadge } from "./comments";

const statusEl        = getElement<HTMLParagraphElement>("compare-status");
const listEl          = getElement<HTMLDivElement>("compare-list");
const controlsEl      = getElement<HTMLDivElement>("compare-controls");
const sortMenuEl      = getElement<HTMLDivElement>("compare-sort-container");
const orderMenuEl     = getElement<HTMLDivElement>("compare-order-container");
const clearBtn        = getElement<HTMLButtonElement>("compare-clear-btn");
const dimensionContainer = getElement<HTMLDivElement>("compare-dimension-container");

// Przełącznik Zajęcia/Egzamin (na razie tylko WPiA UW, patrz post.supports_exam_dimensions -
// odpowiednik DimensionTab w post_detail.ts, ale bez zakładki "Pytania" - porównanie pokazuje
// tylko wymiary z oceną, nie listę pytań egzaminacyjnych). Pokazywany TYLKO gdy choć jedna
// porównywana osoba wspiera ten podział (patrz renderDimensionTabsIfNeeded niżej).
type DimensionTab = "zajecia" | "egzamin";
let activeDimension: DimensionTab = "zajecia";

// Kolejność KART (kto po lewej, kto po prawej) - osobna sprawa od #compare-sort wyżej (ten
// sortuje tylko komentarze WEWNĄTRZ każdej karty). Te same kryteria co w wyszukiwarce listy
// wykładowców (patrz PostSortField/SORT_OPTIONS w browser.ts) - "none" domyślnie, żeby kolejność
// dodawania (koszyk porównania) nie zmieniała się sama pod użytkownikiem, dopóki świadomie nie
// wybierze sortowania.
type CompareOrderField = "none" | PostSortField;
const ORDER_OPTIONS_ZAJECIA: SortMenuOption<CompareOrderField>[] = [
    { value: "none", label: "Bez sortowania (kolejność dodania)" },
    { value: "rating", label: "Ocena" },
    { value: "power", label: "Moc" },
    { value: "most_comments", label: "Najwięcej komentarzy" },
    { value: "surname", label: "Nazwisko" },
    { value: "name", label: "Imię" },
];
// Odpowiednik ORDER_OPTIONS_ZAJECIA wyżej, dla egzaminu - ta sama kategoria (ocena), tylko osobny
// wymiar (Comment.exam_rating, nie rating) i bez "Moc" (bayesowski wzór nie jest tu potrzebny -
// dużo mniej opinii o egzaminie niż o zajęciach na osobę).
const ORDER_OPTIONS_EGZAMIN: SortMenuOption<CompareOrderField>[] = [
    { value: "none", label: "Bez sortowania (kolejność dodania)" },
    { value: "exam_rating", label: "Ocena egzaminu" },
    { value: "most_exam_comments", label: "Najwięcej opinii o egzaminie" },
    { value: "surname", label: "Nazwisko" },
    { value: "name", label: "Imię" },
];
let orderBy: CompareOrderField = "none";

const COMMENT_SORT_OPTIONS_ZAJECIA: SortMenuOption<CommentSortField>[] = [
    { value: "newest", label: "Najnowsze" },
    { value: "oldest", label: "Najstarsze" },
    { value: "best_rating", label: "Najwyższa ocena" },
    { value: "worst_rating", label: "Najniższa ocena" },
    { value: "most_upvotes", label: "Najwięcej polubień" },
];
// Odpowiednik COMMENT_SORT_OPTIONS_ZAJECIA wyżej, dla egzaminu - ten sam sens ("ocena"), tylko
// osobny wymiar (patrz best_exam_rating/worst_exam_rating w comments.ts).
const COMMENT_SORT_OPTIONS_EGZAMIN: SortMenuOption<CommentSortField>[] = [
    { value: "newest", label: "Najnowsze" },
    { value: "oldest", label: "Najstarsze" },
    { value: "best_exam_rating", label: "Najwyższa ocena" },
    { value: "worst_exam_rating", label: "Najniższa ocena" },
    { value: "most_upvotes", label: "Najwięcej polubień" },
];

const COMMENTS_PAGE_SIZE = 3;

interface CardState {
    post: Post;
    comments: Comment[];
    visibleCount: number;
}

let cards: CardState[] = [];
let sortBy: CommentSortField = "newest";

// Ten sam warunek co filterCommentsByDimension w post_detail.ts (patrz notka tam) - liczy się
// KAŻDY wymiar egzaminu, nie tylko exam_difficulty (większość importowanych opinii o egzaminie
// nie ma osobnej liczbowej trudności).
function filterCommentsByDimension(comments: Comment[], dimension: DimensionTab): Comment[] {
    if (dimension === "egzamin") {
        return comments.filter(c =>
            c.exam_rating !== null || c.exam_atmosphere !== null
            || c.exam_difficulty !== null || c.exam_would_take_again !== null
        );
    }
    return comments.filter(c => c.rating !== null);
}

function renderCompactComment(comment: Comment, dimension: DimensionTab): HTMLElement {
    const div = document.createElement("div");
    div.className = "compare-comment";

    const header = document.createElement("div");
    header.className = "compare-comment-header";
    if (dimension === "egzamin") {
        if (comment.exam_rating !== null && comment.exam_rating !== undefined) {
            const icon = showStars(comment.exam_rating);
            icon.title = `Ocena egzaminu: ${EXAM_RATING_LABELS[comment.exam_rating] ?? comment.exam_rating}`;
            header.append(icon);
        }
        if (comment.exam_difficulty !== null && comment.exam_difficulty !== undefined) {
            const easeText = renderEaseOfPassingText(comment.exam_difficulty);
            if (easeText) header.append(easeText);
        }
    } else {
        if (comment.rating !== null) {
            header.append(showStars(comment.rating));
        }
        if (comment.class_difficulty !== null && comment.class_difficulty !== undefined) {
            const easeText = renderEaseOfPassingText(comment.class_difficulty);
            if (easeText) header.append(easeText);
        }
    }
    if (isFandomImportedComment(comment)) {
        header.append(renderFandomSourceBadge());
    } else if (comment.is_from_psych_import) {
        header.append(renderPsychSourceBadge());
    } else if (comment.is_from_wpia_wiki) {
        header.append(renderWpiaWikiSourceBadge());
    } else if (comment.is_from_filozofia_wiki) {
        header.append(renderFilozofiaWikiSourceBadge());
    }
    header.append(whenAdded(comment));

    const body = document.createElement("p");
    body.className = "compare-comment-body";
    // Egzamin: wolimy exam_body (osobne pole z formularza), ale większość opinii egzamin-only z
    // importu wpia-wiki trzyma całą treść w "body" (patrz notka przy Comment.exam_body w
    // models.py) - stąd fallback, nie samo exam_body.
    body.textContent = (dimension === "egzamin" && comment.exam_body) ? comment.exam_body : comment.body;

    div.append(header, body);
    return div;
}

// Odpowiednik showAvgRating (posts.ts) dla wymiaru egzaminu - ta sama struktura (duża liczba +
// gwiazdki + licznik w nawiasie), ten sam widget gwiazdkowy co zajęcia, tylko osobne pole
// (Comment.exam_rating). Gdy są opinie o egzaminie, ale żadna nie ma jeszcze exam_rating (rzadkie,
// ale możliwe przy samym exam_atmosphere/exam_would_take_again z importu), pokazujemy komunikat
// zamiast mylącego "0,0" + puste gwiazdki.
function renderExamHeadline(post: Post): HTMLElement {
    const el = document.createElement("div");
    el.style.display = "inline-flex";
    el.style.alignItems = "center";
    el.style.gap = "0.3em";
    el.classList.add("avg-rating-row");

    const avg = post.avg_exam_rating ?? 0;
    const numExamComments = post.number_of_exam_comments ?? 0;

    if (avg === 0 && numExamComments > 0) {
        const msg = document.createElement("span");
        msg.textContent = "Brak ocen";
        msg.style.textDecoration = "none";
        msg.style.color = "var(--text-muted)";
        el.appendChild(msg);
        return el;
    }

    const number = document.createElement("span");
    number.textContent = avg.toFixed(1).replace(".", ",");
    number.style.textDecoration = "none";

    const icons = showPartialStars(avg);

    const count = document.createElement("span");
    count.textContent = `(${numExamComments})`;
    count.style.textDecoration = "none";

    el.append(number, icons, count);
    return el;
}

function renderCard(state: CardState): HTMLElement {
    const card = document.createElement("div");
    card.className = "compare-card";

    const heading = document.createElement("h2");
    const link = document.createElement("a");
    link.href = `/${state.post.slug}`;
    link.textContent = `${state.post.name} ${state.post.surname}`;
    heading.appendChild(link);

    const isExam = activeDimension === "egzamin";

    // Badge "% poleca" NIE idzie do showAvgRating/renderExamHeadline (tam ląduje w tym samym
    // wierszu co gwiazdki/ikony) - w wąskiej karcie porównania to się nie mieści obok reszty, więc
    // renderujemy go osobno, w swojej własnej linijce pod gwiazdkami.
    const headline = isExam
        ? renderExamHeadline(state.post)
        : showAvgRating(state.post.avg_rating, state.post.number_of_comments, null);
    const wouldTakeAgainBadge = renderWouldTakeAgainBadge(
        isExam ? (state.post.exam_would_take_again_percent ?? null) : state.post.would_take_again_percent
    );
    const avgDifficulty = isExam ? state.post.avg_exam_difficulty : state.post.avg_class_difficulty;
    const difficultyBadge = (avgDifficulty !== null && avgDifficulty !== undefined)
        ? renderEaseOfPassingText(avgDifficulty) : null;
    const distribution = isExam
        ? renderRatingDistribution(state.post.exam_rating_distribution ?? {}, { showLabels: false, labels: EXAM_RATING_LABELS })
        : renderRatingDistribution(state.post.rating_distribution ?? {}, { showLabels: false });

    const commentsSection = document.createElement("div");
    commentsSection.className = "compare-comments";

    const dimensionComments = filterCommentsByDimension(state.comments, activeDimension);
    const sorted = sortComments(dimensionComments, sortBy);
    const visible = sorted.slice(0, state.visibleCount);

    if (visible.length === 0) {
        const empty = document.createElement("p");
        empty.className = "compare-comments-empty";
        empty.textContent = isExam ? "Brak opinii o egzaminie." : "Brak komentarzy.";
        commentsSection.appendChild(empty);
    } else {
        visible.forEach(comment => commentsSection.appendChild(renderCompactComment(comment, activeDimension)));
    }

    if (sorted.length > state.visibleCount) {
        const moreBtn = document.createElement("button");
        moreBtn.type = "button";
        moreBtn.className = "add-comment-btn compare-show-more-btn";
        moreBtn.textContent = `Pokaż więcej (${sorted.length - state.visibleCount})`;
        moreBtn.addEventListener("click", () => {
            state.visibleCount += COMMENTS_PAGE_SIZE;
            rerender();
        });
        commentsSection.appendChild(moreBtn);
    }

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "add-comment-btn compare-remove-btn";
    removeBtn.textContent = "Usuń z porównania";
    removeBtn.addEventListener("click", () => {
        removeFromCompare(state.post.id);
        load();
    });

    card.append(heading, headline);
    if (wouldTakeAgainBadge) card.append(wouldTakeAgainBadge);
    if (difficultyBadge) card.append(difficultyBadge);
    card.append(distribution, commentsSection, removeBtn);
    return card;
}

// Wyrównuje wysokość nagłówków (imię+nazwisko) do najwyższego w bieżącym zestawie kart. CSS
// samo (bez subgrida) nie potrafi wyrazić "wysokość = maksimum z treści rodzeństwa" - poprzednio
// rozwiązywał to statyczny min-height:2.5em w CSS, zarezerwowany na 2 linijki ZAWSZE, nawet gdy
// żadne z wyświetlanych nazwisk się nie łamie. Mierzymy więc w JS: jeśli wszystkie nazwiska
// mieszczą się w 1 linijce, żadna karta nie marnuje miejsca na drugą "na wszelki wypadek"; jeśli
// choć jedno się łamie, wszystkie karty i tak zaczynają gwiazdki na tej samej wysokości. Karty
// mają stałą szerokość (patrz .compare-card w CSS), więc łamanie zależy tylko od długości
// nazwiska, nie od szerokości okna - nie trzeba nasłuchiwać resize.
function alignCardHeadings(): void {
    const headings = Array.from(listEl.querySelectorAll<HTMLHeadingElement>(".compare-card h2"));
    if (headings.length === 0) return;

    headings.forEach(h => { h.style.minHeight = ""; });
    const tallest = Math.max(...headings.map(h => h.getBoundingClientRect().height));
    headings.forEach(h => { h.style.minHeight = `${tallest}px`; });
}

// "cards" zostaje ZAWSZE w kolejności dodania (koszyka) - sortujemy dopiero tu, na potrzeby
// wyświetlenia, żeby przełączenie z powrotem na "Bez sortowania" odtwarzało oryginalną kolejność
// bez przeładowania strony. Najlepszy wynik po LEWEJ - sortPosts() już zwraca malejąco dla
// rating/power/most_comments/exam_rating/most_exam_comments (patrz posts.ts), więc pierwszy
// element w tablicy = pierwsza karta od lewej w .compare-scroll (flex row, bez RTL).
function getOrderedCards(): CardState[] {
    if (orderBy === "none") return cards;

    const byPostId = new Map(cards.map(state => [state.post.id, state]));
    return sortPosts(cards.map(state => state.post), orderBy)
        .map(post => byPostId.get(post.id))
        .filter((state): state is CardState => state !== undefined);
}

function rerender(): void {
    listEl.replaceChildren();
    getOrderedCards().forEach(state => listEl.appendChild(renderCard(state)));
    alignCardHeadings();
}

let orderMenu: { element: HTMLElement, setValue: (value: CompareOrderField) => void };
let sortMenu: { element: HTMLElement, setValue: (value: CommentSortField) => void };

// Menu sortowania mają STAŁĄ listę opcji zbudowaną raz przy renderSortMenu (patrz generics.ts) -
// żeby podmienić opcje (Ocena -> Trudność egzaminu i in.) przy przełączeniu zakładki, trzeba
// zbudować menu na nowo, nie da się ich "przeetykietować" w miejscu.
function rebuildSortMenus(): void {
    const isExam = activeDimension === "egzamin";

    // Domyślnie wg gwiazdek (na wyraźną prośbę) - nie "Bez sortowania" jak dawniej, żeby
    // najlepiej oceniani od razu byli po lewej, bez ręcznego przełączania sortowania za każdym
    // razem.
    orderBy = isExam ? "exam_rating" : "rating";
    orderMenuEl.replaceChildren();
    orderMenu = renderSortMenu(isExam ? ORDER_OPTIONS_EGZAMIN : ORDER_OPTIONS_ZAJECIA, orderBy, (value) => {
        orderBy = value;
        rerender();
    });
    orderMenuEl.appendChild(orderMenu.element);

    sortBy = "newest";
    sortMenuEl.replaceChildren();
    sortMenu = renderSortMenu(isExam ? COMMENT_SORT_OPTIONS_EGZAMIN : COMMENT_SORT_OPTIONS_ZAJECIA, sortBy, (value) => {
        sortBy = value;
        rerender();
    });
    sortMenuEl.appendChild(sortMenu.element);
}

// Przełącznik Zajęcia/Egzamin - pokazywany TYLKO gdy choć jedna porównywana osoba jest z
// wydziału wspierającego ten podział (na razie WPiA UW) - inaczej byłby to martwy kontrolek na
// stronach porównujących zwykłych wykładowców. Ta sama struktura DOM/klasy CSS co dimension-tabs
// w post_detail.ts (.dimension-tabs/.dimension-tab-btn), dla spójnego wyglądu.
function renderDimensionTabsIfNeeded(): void {
    dimensionContainer.replaceChildren();
    const anySupportsExam = cards.some(c => c.post.supports_exam_dimensions);
    if (!anySupportsExam) {
        activeDimension = "zajecia";
        return;
    }

    const TABS: { value: DimensionTab, label: string }[] = [
        { value: "zajecia", label: "Zajęcia" },
        { value: "egzamin", label: "Egzamin" },
    ];

    const nav = document.createElement("div");
    nav.className = "dimension-tabs";
    nav.setAttribute("role", "tablist");

    const buttons = TABS.map(tab => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "dimension-tab-btn";
        btn.textContent = tab.label;
        btn.setAttribute("role", "tab");
        btn.setAttribute("aria-selected", String(tab.value === activeDimension));
        btn.classList.toggle("active", tab.value === activeDimension);
        btn.addEventListener("click", () => {
            if (activeDimension === tab.value) return;
            activeDimension = tab.value;
            buttons.forEach((b, i) => {
                b.classList.toggle("active", TABS[i].value === tab.value);
                b.setAttribute("aria-selected", String(TABS[i].value === tab.value));
            });
            rebuildSortMenus();
            rerender();
        });
        nav.appendChild(btn);
        return btn;
    });

    dimensionContainer.appendChild(nav);
}

async function load(): Promise<void> {
    const ids = getCompareIds();
    listEl.replaceChildren();
    dimensionContainer.replaceChildren();
    cards = [];

    if (ids.length === 0) {
        controlsEl.style.display = "none";
        statusEl.textContent = "Nie masz jeszcze żadnych wykładowców do porównania. Dodaj ich przyciskiem \"Porównaj\" na liście.";
        return;
    }

    controlsEl.style.display = "none";
    statusEl.textContent = "Ładowanie...";

    const postsRes = await api.getPostsByIds(ids);
    if (!postsRes.ok) {
        statusEl.textContent = `Błąd: ${postsRes.error}`;
        return;
    }

    // Zachowujemy kolejność dodawania (kolejność z koszyka), nie kolejność z odpowiedzi API
    const byId = new Map(postsRes.data.posts.map(post => [post.id, post]));
    const orderedPosts = ids
        .map(id => byId.get(id))
        .filter((post): post is Post => post !== undefined);

    if (orderedPosts.length === 0) {
        statusEl.textContent = "Wybrani wykładowcy nie są już dostępni.";
        return;
    }

    const commentResults = await Promise.all(orderedPosts.map(post => api.getComments(post.id)));

    cards = orderedPosts.map((post, index) => {
        const res = commentResults[index];
        return {
            post,
            comments: res.ok ? res.data.comments : [],
            visibleCount: COMMENTS_PAGE_SIZE,
        };
    });

    statusEl.textContent = "";
    controlsEl.style.display = "";
    renderDimensionTabsIfNeeded();
    rerender();
}

rebuildSortMenus();

clearBtn.addEventListener("click", () => {
    clearCompare();
    load();
});

load();
